/**
 * StaffHubPage — 직원
 *
 * 직원 출입 기록과 총무 업무를 한 화면에 모은다.
 * 종전엔 출입 기록에 학생과 직원이 섞여 있어 직원만 따로 볼 수단이 없었고,
 * 총무 업무·직원 관리·전달사항이 서로 다른 메뉴에 흩어져 있었다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import { ownerLayoutService } from '../services/OwnerLayoutService';
import StaffTaskNowPanel from '../components/dashboard/StaffTaskNowPanel';
import './StaffHubPage.css';

type TabKey = 'access' | 'tasks';

interface AccessLog {
  id: string;
  timestamp: string;
  user_id: string;
  user_name: string;
  action: string;
  method: string;
  outing_type?: string | null;
}

interface TaskItem {
  id: string;
  shift_label: string;
  start_time: string | null;
  end_time: string | null;
  place: string;
  content: string;
  status: 'done' | 'now' | 'overdue' | 'upcoming';
  checked_at: string | null;
  checked_by_name: string;
  note: string;
}

interface TaskSummary {
  date: string;
  total: number;
  done: number;
  overdue: number;
  items: TaskItem[];
}

const getToken = () =>
  localStorage.getItem('auth_access_token') ||
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token');

const getOrgId = (): string => {
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const parsed = JSON.parse(authStorage);
      if (parsed.state?.user?.organization_id) return parsed.state.user.organization_id;
    } catch {
      /* 파싱 실패 시 아래 폴백 */
    }
  }
  return localStorage.getItem('organization_id') || '';
};

const todayStr = () => {
  const now = new Date();
  const kst = new Date(now.getTime() + (now.getTimezoneOffset() * 60 + 9 * 3600) * 1000);
  return `${kst.getFullYear()}-${String(kst.getMonth() + 1).padStart(2, '0')}-${String(kst.getDate()).padStart(2, '0')}`;
};

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString('ko-KR', {
    timeZone: 'Asia/Seoul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

const ENTER_ACTIONS = ['entry', 'enter', 'outing_return'];

const ACTION_LABEL: Record<string, string> = {
  entry: '입실',
  enter: '입실',
  exit: '퇴실',
  outing: '외출',
  outing_return: '복귀',
};

const METHOD_LABEL: Record<string, string> = {
  face: '얼굴',
  phone: '번호',
  qr: 'QR',
  owner_fix: '원장 보정',
  manual: '수동',
};

const STATUS_LABEL: Record<TaskItem['status'], string> = {
  done: '완료',
  now: '지금 할 차례',
  overdue: '지났는데 미체크',
  upcoming: '아직',
};

interface StaffDay {
  userId: string;
  name: string;
  firstEnter: string | null;
  lastExit: string | null;
  count: number;
  state: '근무 중' | '외출 중' | '퇴근';
}

const StaffHubPage: React.FC = () => {
  const navigate = useNavigate();
  // 대시보드 '전체 보기'에서 오면 총무 업무 탭부터
  const [tab, setTab] = useState<TabKey>(
    new URLSearchParams(window.location.search).get('tab') === 'tasks' ? 'tasks' : 'access'
  );
  const [date, setDate] = useState(todayStr());

  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [tasks, setTasks] = useState<TaskSummary | null>(null);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({
        role: 'staff',
        start_date: date,
        end_date: date,
        limit: '1000',
      });
      const orgId = getOrgId();
      if (orgId) params.append('org_id', orgId);

      const res = await fetch(`${API_BASE_URL}/v1/owner/entrance-exit/logs?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error('출입 기록을 불러오지 못했습니다.');
      setLogs(json.data?.logs || []);
      setError('');
    } catch (e: any) {
      setError(e.message);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [date]);

  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/owner/staff-tasks/today?task_date=${date}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
          },
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error('총무 업무를 불러오지 못했습니다.');
      setTasks(json.data || null);
    } catch (e: any) {
      setError(e.message);
      setTasks(null);
    } finally {
      setTasksLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadLogs();
    loadTasks();
  }, [loadLogs, loadTasks]);

  const toggleCheck = async (item: TaskItem) => {
    setBusyId(item.id);
    try {
      const done = item.status === 'done';
      // 체크는 본문으로, 취소는 주소로 날짜를 받는다. 취소에 날짜를 안 붙이면
      // 지난 날짜를 보다가 눌렀을 때 오늘 체크가 지워진다.
      const url = done
        ? `${API_BASE_URL}/api/v1/owner/staff-tasks/${item.id}/check?task_date=${date}`
        : `${API_BASE_URL}/api/v1/owner/staff-tasks/${item.id}/check`;
      const res = await fetch(url, {
        method: done ? 'DELETE' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
        body: done ? undefined : JSON.stringify({ task_date: date }),
      });
      if (!res.ok) throw new Error('처리하지 못했습니다.');
      await loadTasks();
      setError('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  // 직원별 하루 요약 — 첫 입실, 마지막 퇴실, 지금 상태
  const staffDays = useMemo<StaffDay[]>(() => {
    const byUser = new Map<string, AccessLog[]>();
    logs.forEach((log) => {
      if (!log.user_id) return;
      const list = byUser.get(log.user_id) || [];
      list.push(log);
      byUser.set(log.user_id, list);
    });

    const rows: StaffDay[] = [];
    byUser.forEach((list, userId) => {
      const sorted = [...list].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      const firstEnter = sorted.find((l) => ENTER_ACTIONS.includes(l.action));
      const exits = sorted.filter((l) => l.action === 'exit');
      const last = sorted[sorted.length - 1];

      let state: StaffDay['state'] = '퇴근';
      if (last) {
        if (ENTER_ACTIONS.includes(last.action)) state = '근무 중';
        else if (last.action === 'outing') state = '외출 중';
      }

      rows.push({
        userId,
        name: sorted[0]?.user_name || '알 수 없음',
        firstEnter: firstEnter ? timeOf(firstEnter.timestamp) : null,
        lastExit: exits.length ? timeOf(exits[exits.length - 1].timestamp) : null,
        count: sorted.length,
        state,
      });
    });

    return rows.sort((a, b) => (a.firstEnter || '99').localeCompare(b.firstEnter || '99'));
  }, [logs]);

  const sortedLogs = useMemo(
    () =>
      [...logs].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ),
    [logs]
  );

  const go = (path: string) => navigate(ownerLayoutService.getOwnerPath(path));

  return (
    <div className="staff-hub">
      <div className="shb-header">
        <div>
          <h1>직원</h1>
          <p className="shb-sub">
            직원 출입 기록과 총무 업무를 한 곳에서 봅니다. 학생 기록은 섞이지 않습니다.
          </p>
        </div>
        <div className="shb-links">
          <button className="shb-btn" onClick={() => go('/owner/staff')}>
            직원 관리
          </button>
          <button className="shb-btn" onClick={() => go('/owner/staff-memos')}>
            메모 / 전달사항
          </button>
          <button className="shb-btn" onClick={() => go('/owner/staff-tasks')}>
            총무 업무 편집
          </button>
        </div>
      </div>

      <div className="shb-toolbar">
        <div className="shb-tabs">
          <button
            className={`shb-tab ${tab === 'access' ? 'active' : ''}`}
            onClick={() => setTab('access')}
          >
            출입 기록
          </button>
          <button
            className={`shb-tab ${tab === 'tasks' ? 'active' : ''}`}
            onClick={() => setTab('tasks')}
          >
            총무 업무
            {tasks && tasks.total > 0 && (
              <span className="shb-tab-count">
                {tasks.done}/{tasks.total}
              </span>
            )}
          </button>
        </div>
        <input
          className="shb-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      {error && <div className="shb-error">{error}</div>}

      {tab === 'access' && (
        <>
          {logsLoading ? (
            <div className="shb-empty">불러오는 중…</div>
          ) : staffDays.length === 0 ? (
            <div className="shb-empty">이 날 직원 출입 기록이 없습니다.</div>
          ) : (
            <>
              <div className="shb-card">
                <h2>직원별 요약</h2>
                <table className="shb-table">
                  <thead>
                    <tr>
                      <th>이름</th>
                      <th>첫 입실</th>
                      <th>마지막 퇴실</th>
                      <th>기록</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffDays.map((row) => (
                      <tr key={row.userId}>
                        <td className="shb-name">{row.name}</td>
                        <td>{row.firstEnter || '-'}</td>
                        <td>{row.lastExit || '-'}</td>
                        <td>{row.count}건</td>
                        <td>
                          <span className={`shb-state ${row.state === '퇴근' ? 'off' : 'on'}`}>
                            {row.state}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="shb-card">
                <h2>기록 전체 ({sortedLogs.length}건)</h2>
                <table className="shb-table">
                  <thead>
                    <tr>
                      <th>시각</th>
                      <th>이름</th>
                      <th>구분</th>
                      <th>방식</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{timeOf(log.timestamp)}</td>
                        <td className="shb-name">{log.user_name}</td>
                        <td>
                          {ACTION_LABEL[log.action] || log.action}
                          {log.outing_type ? ` (${log.outing_type})` : ''}
                        </td>
                        <td className="shb-muted">{METHOD_LABEL[log.method] || log.method}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'tasks' && date === todayStr() && (
        <div className="shb-card" style={{ marginBottom: 12 }}>
          <h2>지금 업무</h2>
          <StaffTaskNowPanel onChanged={loadTasks} />
        </div>
      )}

      {tab === 'tasks' && (
        <div className="shb-card">
          {tasksLoading ? (
            <div className="shb-empty">불러오는 중…</div>
          ) : !tasks || tasks.items.length === 0 ? (
            <div className="shb-empty">
              이 날 할 총무 업무가 없습니다. 항목은 [총무 업무 편집]에서 추가합니다.
            </div>
          ) : (
            <>
              <h2>
                {tasks.date} · 완료 {tasks.done}/{tasks.total}
                {tasks.overdue > 0 && <span className="shb-overdue"> · 미체크 {tasks.overdue}</span>}
              </h2>
              <ul className="shb-tasks">
                {tasks.items.map((item) => (
                  <li key={item.id} className={`shb-task ${item.status}`}>
                    <div className="shb-task-time">
                      {item.start_time}
                      {item.end_time && item.end_time !== item.start_time ? `~${item.end_time}` : ''}
                    </div>
                    <div className="shb-task-body">
                      <div className="shb-task-content">{item.content}</div>
                      <div className="shb-muted">
                        {item.shift_label}
                        {item.place ? ` · ${item.place}` : ''}
                        {item.checked_at ? ` · ${item.checked_at} ${item.checked_by_name}` : ''}
                      </div>
                    </div>
                    <div className="shb-task-right">
                      <span className={`shb-badge ${item.status}`}>{STATUS_LABEL[item.status]}</span>
                      <button
                        className="shb-btn"
                        disabled={busyId === item.id}
                        onClick={() => toggleCheck(item)}
                      >
                        {item.status === 'done' ? '체크 취소' : '완료'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffHubPage;
